// 📁 components/Settings/Modules/ModuleCard.jsx
//
// Reusable card for a module (product or integration).
//
// Props:
//   logo, label, description, enabled, onToggle — present-day usage.
//   disabled, disabledReason                    — reserved for subscription
//                                                  gating. Unused today.
//
// When subscription entitlements land:
//   - `disabled = true`   when the org's plan doesn't include this module
//   - `disabledReason`    string like "Upgrade to Pro to enable Rooms"
// The card will render a lock icon, render the toggle non-interactive, and
// surface the reason in a tooltip. No refactor required — just pass the
// props.

import React from 'react';
import { LockOutlined } from '@ant-design/icons';
import { Card, Flex, Switch, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Paragraph } = Typography;

/**
 * @param {Object} props
 * @param {string}   props.logo           - Image source (imported asset)
 * @param {string}   props.label
 * @param {string}   props.description
 * @param {boolean}  props.enabled
 * @param {(next: boolean) => void} props.onToggle
 * @param {boolean}  [props.disabled]        - Reserved for subscription gating
 * @param {string}   [props.disabledReason]  - Reserved for subscription gating
 */
const ModuleCard = ({ logo, label, description, enabled, onToggle, disabled = false, disabledReason = null }) => {
	const switchNode = <Switch checked={enabled} disabled={disabled} onChange={onToggle} />;

	return (
		<Card
			style={{
				width: 320,
				opacity: disabled ? 0.6 : 1
			}}
			styles={{ body: { padding: 20 } }}
		>
			<Flex vertical gap={12}>
				<Flex justify='space-between' align='flex-start'>
					<Flex>{logo}</Flex>
					{disabled && disabledReason ? (
						<Tooltip title={disabledReason}>
							<Tag icon={<LockOutlined />} color='default'>
								Locked
							</Tag>
						</Tooltip>
					) : (
						<Tag color={enabled ? 'success' : 'default'}>{enabled ? 'Active' : 'Inactive'}</Tag>
					)}
				</Flex>

				<Title level={5} style={{ margin: 0 }}>
					{label}
				</Title>

				<Paragraph type='secondary' style={{ margin: 0, minHeight: 60 }}>
					{description}
				</Paragraph>

				<Flex justify='flex-end'>
					{disabled && disabledReason ? (
						<Tooltip title={disabledReason}>
							{/* span wrapper needed for disabled-switch tooltips */}
							<span>{switchNode}</span>
						</Tooltip>
					) : (
						switchNode
					)}
				</Flex>
			</Flex>
		</Card>
	);
};

export default ModuleCard;
